import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Divider } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import { TravellerEdit } from "@/config/app-constant";
import { useRenewalStateContext } from "@/providers/renewal-state-provider";
import { TravellersAccordionType } from "@/schema/renewal-page/middle-panel.schema";
import { RootState } from "@/store/root-reducer";
import { Traveller } from "@/types/Traveller";
import { capitalizeFirstLetter } from "@/utils/common.utils";

import PlusIcon from "./plus-icon/plus-icon";
import TravellersDetailsRow from "./travellers-details-row/travellers-details-row";

type TravellersContentProps = {
  travellersAccordionData: TravellersAccordionType;
};

function TravellersContent({
  travellersAccordionData,
}: Readonly<TravellersContentProps>) {
  const router = useRouter();
  const quote = useSelector((state: RootState) => state.quote);
  const { travellerDetails, tripDetails } = quote.attributes;
  const { updateLastEditAccordion } = useRenewalStateContext();

  const partyType = useMemo(() => {
    if (!tripDetails?.partyType) return "";

    const partyTypeMapping = {
      OTHER: "Group",
      FAMILY: "Family",
      COUPLE: "Couple",
      INDIVIDUAL: "Individual",
      SINGLE_PARENT_FAMILY: "Family",
    };

    return tripDetails?.partyType
      ? partyTypeMapping[
          tripDetails?.partyType as keyof typeof partyTypeMapping
        ]
      : "";
  }, [tripDetails?.partyType]);

  return (
    <div>
      <div data-testid="traveller-sub-section">
        <p className="font-body-font text-2xs font-[400] leading-[24px] text-text-functional-subtle">
          {travellersAccordionData?.typeLabel}
        </p>
        <TravellersDetailsRow
          typeNameAge={partyType}
          editCtaLabel={travellersAccordionData?.editCtaLabel}
          id="traveller-type-edit"
        />
        <Divider margin={12} color="#DCDCDC" />
      </div>

      <div className="mt-spacing-l md:mt-spacing-m lg:mt-spacing-l">
        {travellerDetails?.traveller?.map((traveller: Traveller) => (
          <div key={traveller.encryptedId}>
            <TravellersDetailsRow
              typeNameAge={`${capitalizeFirstLetter(
                traveller.firstName ?? ""
              )} ${capitalizeFirstLetter(traveller.lastName ?? "")}, ${
                traveller.age
              }`}
              editCtaLabel={travellersAccordionData?.editCtaLabel}
              id={`traveller-${traveller.sequence}-edit`}
            />
            <Divider margin={12} color="#DCDCDC" />
          </div>
        ))}

        <div className="w-[162px]">
          <LabelButtonWrapper
            label={travellersAccordionData?.addCtaLabel}
            hierarchy="secondary"
            isLeftIcon
            className="!h-[48px]"
            leftIcon={<PlusIcon />}
            id="add-traveller-button"
            onClick={() => {
              router.push(`/cover-for?${TravellerEdit}=true`);
              updateLastEditAccordion("travellers");
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default TravellersContent;
